import type { paths } from '@crossbeam/openapi';

import type { PartnerScoreBucket } from '@/types/partners';
import type { SegmentType } from '@/types/populations';

export const INDIVIDUAL_RECORD_PATH =
  '/v0.3/records/{source-id}/{source-primary-key}';

type ResponseUnionType =
  paths[typeof INDIVIDUAL_RECORD_PATH]['get']['responses'][200]['content']['application/json'];

type ResponseForSectionType<Section> = Extract<
  ResponseUnionType,
  { section: Section }
>;

export type RecordSectionParamType = ResponseUnionType['section'];
export type RecordSectionType = Exclude<RecordSectionParamType, 'all'>;

export type RecordFieldType =
  ResponseForSectionType<'master'>['own']['fields'][number];

export type RecordCollectionType = {
  items: { fields: RecordFieldType[] }[];
};

export type RecordContactCollectionType =
  ResponseForSectionType<'contacts'>['own']['contacts'];

export type RecordContactType = RecordContactCollectionType['items'][number];

export type RecordOpportunityCollectionType =
  ResponseForSectionType<'opportunities'>['own']['opportunities'];

export type RecordOpportunityType =
  RecordOpportunityCollectionType['items'][number];

export type RecordPaginationType =
  ResponseForSectionType<'master'>['partners']['pagination'];

export type RecordOwnBySectionType = {
  [Section in RecordSectionParamType]: ResponseForSectionType<Section>['own'];
};

export type RecordResponseType<Section extends RecordSectionParamType = 'all'> =
  ResponseForSectionType<Section>;

export type RecordPartnerType<Section extends RecordSectionParamType = 'all'> =
  RecordResponseType<Section>['partners']['items'][number];

export type RecordPartnerAnyType = RecordPartnerType<RecordSectionParamType>;

export type RecordFiltersType = {
  partner_search: string;
  partner_org_ids: number[];
  partner_tag_ids: number[];
  partner_segments: SegmentType[];
  partner_score_bucket: NonNullable<PartnerScoreBucket>[];
};

export type RecordQueryType = {
  section?: RecordSectionType;
  page?: number;
  limit?: number;
  partner_search?: string;
  partner_org_ids?: number[];
  partner_tag_ids?: number[];
  partner_segments?: SegmentType[];
  partner_score_bucket?: NonNullable<PartnerScoreBucket>[];
};

export type RecordOverviewPartnerType = {
  partner_organization_uuid: string;
  partner_name: string;
  shared_with_us_population_ids: number[];
  partner_score_bucket: PartnerScoreBucket;
  partner_account_owner: { name: string } | null;
  contacts_common_count: number;
  contacts_new_count: number;
  open_opportunities_count: number;
  data_source: { integration_type: string; label: string } | null;
  fields?: RecordFieldType[];
};
